import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { chipReference, variableReferenceKey } from './templateSerialization';

const pluginKey = new PluginKey('invalidVariableChipDecoration');

export interface InvalidVariableChipDecorationStorage {
  /** `variableReferenceKey` of each variable the node can read */
  validReferenceKeys: Set<string>;
}

declare module '@tiptap/core' {
  interface Storage {
    invalidVariableChipDecoration: InvalidVariableChipDecorationStorage;
  }
}

/**
 * Applies red styling to "variable chips" (the inline badges that stand for
 * a variable reference) when the node can't read the variable the chip
 * refers to: its node isn't an ancestor, or doesn't publish that variable
 * from that source.
 */
interface InvalidVariableChipDecorationOptions {
  /** CSS class to apply to invalid chips (from CSS module) */
  className: string;
}

export const InvalidVariableChipDecoration = Extension.create<
  InvalidVariableChipDecorationOptions,
  InvalidVariableChipDecorationStorage
>({
  name: 'invalidVariableChipDecoration',

  addOptions() {
    return {
      className: 'invalidChip',
    };
  },

  addStorage() {
    return {
      validReferenceKeys: new Set<string>(),
    };
  },

  addProseMirrorPlugins() {
    const extensionStorage = this.storage;
    const { className } = this.options;

    return [
      new Plugin({
        key: pluginKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            const { validReferenceKeys } = extensionStorage;

            state.doc.descendants((node, pos) => {
              if (node.type.name === 'mention') {
                const key = variableReferenceKey(chipReference(node.attrs));
                if (!validReferenceKeys.has(key)) {
                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                      class: className,
                    })
                  );
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
